<template>
  <div class="agenda-table-div">
    <DataTable :value="sortedAgendaDataWithSerialNumbers" paginator :alwaysShowPaginator=false :rows=7 tableStyle=" min-width:
        10rem" tableClass="editable-cells-table" @row-click="emitSelectPatient">
      <template v-if="!agendaDataLength && !isLoading" #empty>
        <p class="no-records"> No Patients in Agenda</p>
      </template>
      <Column field="serialNumber" header="" sortable>
      </Column>
      <Column field="given" header="PATIENT NAME" sortable>
        <template #body="{ data }">
          <span :class="uneditableClass">
            {{ data.given }} {{ data.family }}
          </span>
        </template>
      </Column>
      <Column field="caregiver_name" header="REQUESTING PHYSICIAN" sortable>
        <template #body="{ data }">
          <span :class="uneditableClass">
            {{ data.name }}
          </span>
        </template>
      </Column>
      <Column field="gender" header="AGE/GENDER">
        <template #body="{ data }">
          <span :class="uneditableClass">
            {{ calculateAge(data.birthdate) }}/{{ data.gender }}
          </span>
        </template>
      </Column>
      <Column field="diagnosis" header="DIAGNOSIS" class="edit-agenda-diagnosis">
        <template #body="{ data, field }">
          <span v-if="!isEditMode" :class="uneditableClass">{{ data[field] }}</span>
          <select v-else v-model="data[field]" :class="editColorClass" @focus="storeCellValue(data[field])"
            @change="emitCellEditComplete(data, data[field], field, originalCellValue)">
            <option v-for="item in data['diagnosisArray']" :key="item.icd10_code" :value="item.description">{{
      item.description }}</option>
          </select>
        </template>
      </Column>
      <Column v-if="editToggle" field="tb_name" header="TUMOR BOARD" class="edit-agenda-tb">
        <template #body="{ data, field }">
          <select v-model="data[field]" :class="editColorClass" @focus="storeCellValue(data[field])"
            @change="emitCellEditComplete(data, data[field], field, originalCellValue)">
            <option v-for="item in tumorBoardsDropdown" :key="item.tb_id" :value="item.tb_name">{{ item.tb_name }}
            </option>
          </select>
        </template>
      </Column>
      <Column field="notes" header="NOTES">
        <template #body="{ data, field }">
          <span v-if="!isEditMode" :class="uneditableClass">{{ data[field] }}</span>
          <input v-else v-model="data[field]" @focus="storeCellValue(data[field])"
            @change="emitCellEditComplete(data, data[field], field, originalCellValue)" :class="editColorClass"
            type="text" />
        </template>
      </Column>
      <Column field="priority" header="PRIORITY" sortable>
        <template #body="{ data, field }">
          <span v-if="!isEditMode" :class="uneditableClass">{{ data[field] }}</span>
          <select v-else v-model="data[field]" :class="editColorClass" :disabled="!isEditMode"
            @focus="storeCellValue(data[field])"
            @change="emitCellEditComplete(data, data[field], field, originalCellValue)">
            <option v-for="item in priorityOptions" :key="item.id" :value="item.priority">{{ item.priority }}
            </option>
          </select>
        </template>
      </Column>
      <Column v-if="editToggle" field="date" header="DATE" sortable class="edit-agenda-date">
        <template #body="{ data, field }">
          <Calendar v-model="data[field]" :manualInput="false" dateFormat="dd-M-yy"
            @focus="storeCellValue(formatDate(data[field]))"
            @update:modelValue="emitCellEditComplete(data, data[field], field, originalCellValue)">
            <template #date="slotProps">
              <strong v-if="isHighlightedDateAgenda(slotProps, data[field])" style="color: var(--Primary)">
                {{ slotProps.date.day }}
              </strong>
              <template v-else>
                {{ slotProps.date.day }}
              </template>
            </template>
          </Calendar>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2024 Omics Data Automation, Inc.
// Copyright (c) 2024 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, defineProps, ref } from 'vue'
import DataTable from 'primevue/datatable'
import Calendar from 'primevue/calendar'
import Column from 'primevue/column'
import { calculateAge, formatDate } from '@/utils/utils.js'

const props = defineProps({
  agendaDataLength: { type: Number, required: true },
  agendaHighlightedDates: { type: Object, required: true },
  editToggle: { type: String, required: true },
  isEditMode: { type: Boolean, required: true },
  isLoading: { type: Boolean, required: true },
  sortedAgendaDataWithSerialNumbers: { type: Array, required: true },
  tumorBoardsDropdown: { type: Array, required: true },
})

const emit = defineEmits(['cell-edit-complete', 'select-patient'])

const originalCellValue = ref(null)
const priorityOptions = ref([
  { priority: 'Urgent', id: 'ug' },
  { priority: 'Standard', id: 'st' },
  { priority: 'Low', id: 'lw' },
])

const editColorClass = computed(() => props.isEditMode ? 'text-input' : 'edit-disabled clickable')
const uneditableClass = computed(() => ['edit-disabled', { clickable: !props.isEditMode }])

const emitSelectPatient = (event) => {
  emit('select-patient', event)
}

const emitCellEditComplete = (data, newValue, field, originalValue) => {
  emit('cell-edit-complete', data, newValue, field, originalValue)
}

const storeCellValue = (value) => originalCellValue.value = value

const isHighlightedDateAgenda = (dateObj) => {
  const formattedDate = `${dateObj.date.year}-${String(dateObj.date.month + 1).padStart(2, '0')}-${String(dateObj.date.day).padStart(2, '0')}`
  return props.agendaHighlightedDates.dates?.includes(formattedDate) || false
}
</script>

<style lang="scss" scoped>
.agenda-table-div {
  margin-bottom: 5%;
  background-color: var(--White);
}

.clickable:hover {
  cursor: pointer;
  color: var(--Primary) !important;
}

.edit-disabled,
.text-input {
  max-width: 9.375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edit-disabled {
  color: var(--black);
  border: none;
  font-family: roboto;
  font-size: 14px;
  font-weight: 400;
}

.no-records {
  color: var(--Black);
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
}

.text-input {
  cursor: pointer;
  color: var(--Primary);
  margin-bottom: 0;
  border: 1px solid var(--Primary);
  height: 2.625rem;
  display: inline-block;
  min-width: 5rem;
}
</style>

<!-- Unfortunately, can't scope some styles for PrimeVue. This means these styles will get applied
 anywhere else the classes exist unless those files also apply styles. -->
<style lang="scss">
.edit-agenda-date {
  min-width: 8.75rem;
}

.edit-agenda-tb {
  min-width: 7.5rem;
}

.p-datepicker {
  z-index: 9999 !important;
}

.p-calendar .p-inputtext {
  border: 1px solid var(--Primary);
  color: var(--Primary);
  cursor: pointer;
}

.agenda-table-div .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
  font-family: 'Roboto';
}

.agenda-table-div .p-datatable .p-datatable-tbody>tr>td {
  font-size: 14px;
  font-weight: 400;
  color: var(--Black);
  font-family: 'Roboto';
}

.agenda-table-div .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.agenda-table-div .p-datatable .p-datatable-tbody>tr>td.notes-column {
  min-width: 9.375rem;
}

.agenda-table-div .p-datatable .p-datatable-tbody>tr>td.notes-column input {
  width: 100%;
  min-width: 9.375rem;
}
</style>
